import * as React from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Button, Text } from '@fragments-sdk/ui';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import {
  cheapestPlanWithGovernanceAutomation,
  hasGovernanceAutomation,
  PLANS,
  type PlanId,
} from '../../../../convex/billing/plans';
import { DashboardLayout, WorkspacePage } from '@/components/layout';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  buildFindingsPath,
  buildGovernancePath,
  buildProjectSearch,
  buildSettingsPath,
  isDemoPath,
} from '@/lib/routes';
import {
  DEMO_TOUR_STEPS,
  DEMO_TOUR_STORAGE_KEY,
  DASHBOARD_TOUR_STEPS,
  TOUR_STORAGE_KEY,
  TourProvider,
  type TourStep,
  useTour,
} from '@/components/tour';
import {
  DashboardSurface,
  DashboardSurfaceSkeleton,
  EMPTY_DASHBOARD_METRICS,
  type DashboardFixScenario,
  type DashboardFixScenarioState,
} from './dashboard-surface';
import {
  ScanProgressBanner,
  useScanBannerState,
  type ScanBannerState,
} from '@/components/scan-progress';
import { useManualRescan } from '@/page/findings/components/repository/use-manual-rescan';
import type { DashboardDrillTarget } from './dashboard-brief';
import { useDashboardData } from './use-dashboard-data';

export function DashboardView() {
  const { pathname } = useLocation();
  const isDemo = isDemoPath(pathname);
  const steps = isDemo ? DEMO_TOUR_STEPS : DASHBOARD_TOUR_STEPS;
  const storageKey = isDemo ? DEMO_TOUR_STORAGE_KEY : TOUR_STORAGE_KEY;

  return (
    <DashboardLayout activeNav="Dashboard" flush>
      <DashboardContent steps={steps} storageKey={storageKey} />
    </DashboardLayout>
  );
}

function DashboardContent({
  steps,
  storageKey,
}: {
  steps: TourStep[];
  storageKey: string;
}) {
  const { metrics, isLoading } = useDashboardData();
  const { selectedProject, selectedProjectId, resolvedSlug } =
    useProjectContext();
  const scanBannerState = useScanBannerState({
    projectId: selectedProjectId,
    repoFullName: selectedProject?.repoFullName,
  });
  const canAutoStartTour = !isLoading && !scanBannerState.isLive;

  return (
    <TourProvider
      steps={steps}
      storageKey={storageKey}
      autoStart={canAutoStartTour}
    >
      <DashboardContentBody
        metrics={metrics}
        isLoading={isLoading}
        selectedProject={selectedProject}
        selectedProjectId={selectedProjectId}
        resolvedSlug={resolvedSlug}
        scanBannerState={scanBannerState}
      />
    </TourProvider>
  );
}

function DashboardContentBody({
  metrics,
  isLoading,
  selectedProject,
  selectedProjectId,
  resolvedSlug,
  scanBannerState,
}: {
  metrics: ReturnType<typeof useDashboardData>['metrics'];
  isLoading: boolean;
  selectedProject: ReturnType<typeof useProjectContext>['selectedProject'];
  selectedProjectId: ReturnType<typeof useProjectContext>['selectedProjectId'];
  resolvedSlug: ReturnType<typeof useProjectContext>['resolvedSlug'];
  scanBannerState: ScanBannerState;
}) {
  const navigate = useNavigate();
  const { active, restart } = useTour();
  const { org } = useOrgContext();
  const orgSlug = org?.slug ?? null;
  const retryScan = useManualRescan({
    orgId: org?._id,
    projectId: selectedProjectId,
    repoFullName: selectedProject?.repoFullName ?? undefined,
  });
  const plan = (org?.plan ?? 'free') as PlanId;
  const canUseGovernanceAutomation = hasGovernanceAutomation(plan);
  const requiredAutomationPlan =
    cheapestPlanWithGovernanceAutomation() ?? 'pro';
  const latestReadiness = useQuery(
    api.registrar.getLatestForProject,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );
  const [fixScenarioState, setFixScenarioState] =
    React.useState<DashboardFixScenarioState>({ status: 'idle' });
  const [activeFixScenario, setActiveFixScenario] =
    React.useState<DashboardFixScenario | null>(null);

  const recommendedFixScenario =
    metrics?.recommendedFixScenario ?? activeFixScenario;
  const displayedMetrics =
    metrics && metrics.recommendedFixScenario !== recommendedFixScenario
      ? { ...metrics, recommendedFixScenario }
      : metrics;

  React.useEffect(() => {
    if (
      !metrics?.recommendedFixScenario ||
      fixScenarioState.status !== 'idle'
    ) {
      return;
    }
    setActiveFixScenario(metrics.recommendedFixScenario);
  }, [fixScenarioState.status, metrics?.recommendedFixScenario]);

  React.useEffect(() => {
    setFixScenarioState({ status: 'idle' });
    setActiveFixScenario(null);
  }, [selectedProjectId]);

  if (isLoading) {
    return (
      <WorkspacePage>
        <DashboardSurfaceSkeleton
          showTourReplay={!active && !scanBannerState.isLive}
          onRestartTour={restart}
        />
      </WorkspacePage>
    );
  }

  function navTo(path: string, extraSearch?: Record<string, string>) {
    if (!orgSlug) return;
    navigate({
      to: path,
      search: buildProjectSearch(resolvedSlug, extraSearch),
    } as any);
  }

  const onNavigate = orgSlug
    ? (target: DashboardDrillTarget) => {
        if (target === 'billing') {
          navTo(buildSettingsPath(orgSlug), { tab: 'billing' });
        } else if (target === 'findings') navTo(buildFindingsPath(orgSlug));
        else if (target === 'governance') navTo(buildGovernancePath(orgSlug));
        else if (target === 'settings') navTo(buildSettingsPath(orgSlug));
      }
    : undefined;

  async function handleCreateFixScenario(scenario: DashboardFixScenario) {
    if (!org || !selectedProjectId) return;

    setActiveFixScenario(scenario);
    setFixScenarioState({ status: 'running' });
    const startedAt = Date.now();

    try {
      const res = await fetch('/api/govern/autofix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId: org._id,
          projectId: selectedProjectId,
          findingIds: scenario.findingIds,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        prUrl?: string;
        prNumber?: number;
        fixedCount?: number;
        error?: string;
      };
      const remainingDelay = 650 - (Date.now() - startedAt);
      if (remainingDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingDelay));
      }
      if (!res.ok || !data.ok) {
        setFixScenarioState({
          status: 'error',
          error: data.error ?? 'Fix PR could not be created',
        });
        return;
      }

      setFixScenarioState({
        status: 'success',
        prUrl: data.prUrl,
        prNumber: data.prNumber,
        fixedCount: data.fixedCount,
      });
    } catch {
      setFixScenarioState({
        status: 'error',
        error: 'Network error while creating the fix PR',
      });
    }
  }

  return (
    <WorkspacePage>
      <ScanProgressBanner
        projectId={selectedProjectId}
        repoFullName={selectedProject?.repoFullName}
        state={scanBannerState}
        onRetry={retryScan}
      />
      {latestReadiness ? (
        <ClaimedReadinessBanner
          report={latestReadiness as any}
          onConnectSource={() => orgSlug && navTo(buildSettingsPath(orgSlug))}
        />
      ) : null}
      <DashboardSurface
        mode="live"
        metrics={displayedMetrics ?? EMPTY_DASHBOARD_METRICS}
        ciStatus={selectedProject?.governanceCiStatus}
        onNavigate={onNavigate}
        onCreateFixScenario={handleCreateFixScenario}
        fixScenarioState={fixScenarioState}
        automationGate={{
          enabled: canUseGovernanceAutomation,
          currentPlanLabel: PLANS[plan].label,
          requiredPlanLabel: PLANS[requiredAutomationPlan].label,
        }}
        showTourReplay={!active && !scanBannerState.isLive}
        onRestartTour={restart}
      />
    </WorkspacePage>
  );
}

function ClaimedReadinessBanner({
  report,
  onConnectSource,
}: {
  report: {
    title: string;
    score: { overall: number; label: string };
    summary?: {
      inventory?: { components?: number };
      metrics?: {
        apiCoverage?: number;
        exampleCoverage?: number;
        governanceFindings?: number | null;
      };
    };
  };
  onConnectSource?: () => void;
}) {
  return (
    <section
      style={{
        margin: '0 0 16px',
        padding: 16,
        border: '1px solid var(--fui-color-border, #d8dde6)',
        borderRadius: 8,
        display: 'grid',
        gap: 12,
      }}
    >
      <div>
        <Text as="strong" size="sm" weight="semibold">
          Registered design system
        </Text>
        <Text as="div" size="sm" color="secondary">
          Imported from CLI readiness. Connect GitHub to keep this catalog
          current and activate governance checks.
        </Text>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
        }}
      >
        <ReadinessStat label="Score" value={`${report.score.overall}/100`} />
        <ReadinessStat
          label="Components"
          value={report.summary?.inventory?.components ?? '—'}
        />
        <ReadinessStat
          label="API coverage"
          value={formatReadinessPercent(report.summary?.metrics?.apiCoverage)}
        />
        <ReadinessStat
          label="Findings"
          value={report.summary?.metrics?.governanceFindings ?? '—'}
        />
      </div>
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={onConnectSource}
        style={{
          justifySelf: 'start',
        }}
      >
        Connect source repo
      </Button>
    </section>
  );
}

function ReadinessStat({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <Text as="div" size="xs" color="secondary">
        {label}
      </Text>
      <Text as="strong" size="sm" weight="semibold">
        {value}
      </Text>
    </div>
  );
}

function formatReadinessPercent(value: number | undefined) {
  return typeof value === 'number' ? `${Math.round(value)}%` : '—';
}
